<template>
  <div class="max-w-12xl mx-auto text-left catalog-search-wrapper">
    <div
      class="grid grid-cols-12 gap-4 md:h-full"
      :class="{ 'grid--filter': filterBrowserMode }"
    >
      <LoaderComponent v-if="initialLoading" />
      <template v-else>
        <!-- Memberstack is navigating to Stripe; hold the screen so the app
             (or the trial-ended screen) doesn't flash behind the redirect. -->
        <div
          class="col-span-12 self-center flex justify-center px-4 py-8"
          v-if="startingCheckout"
        >
          <div class="checkout-handoff">
            <ProgressSpinner style="width: 42px; height: 42px" strokeWidth="4" />
            <p class="ds-subheading">Taking you to secure checkout…</p>
          </div>
        </div>
        <div
          class="col-span-12 self-center flex justify-center px-4 py-8"
          v-else-if="!isAuthenticated"
        >
          <div class="auth-layout">
            <AuthPanel
              app-name="CurriculumFinder"
              :initial-tab="authInitialTab"
              :invite-token="inviteToken"
              :purchase-tier="pendingPlanTier"
              @authorized="onAuthorized"
              @trial-started="onTrialStarted"
            />
            <!-- What CurriculumFinder is, beside the form (below it on phones). -->
            <LandingIntro class="auth-layout__aside" />
          </div>
        </div>
        <div
          class="col-span-12 self-center flex justify-center px-4 py-8"
          v-else-if="!canViewCatalogs"
        >
          <div class="trial-ended">
            <div class="trial-ended__head">
              <h1 class="ds-heading-display">
                Your free trial <em class="ds-grad-text">has ended</em>
              </h1>
              <p class="ds-subheading">
                Subscribe to keep searching every college catalog in the country.
              </p>
            </div>
            <PricingPlans />
          </div>
        </div>
        <template v-else>
          <!-- FILTER & SORT view: full-width horizontal filters + results table.
               Stays until a PDF catalog is opened, then we switch to the shared
               two-pane viewer below (matching the name-search experience). -->
          <div
            v-if="filterBrowserMode"
            class="filter-browser col-span-12 flex flex-col"
          >
            <div class="app-card filter-card flex flex-col flex-1 overflow-hidden">
              <div
                class="app-card__body filter-card__body flex flex-col flex-1 overflow-hidden min-h-0"
              >
                <header class="app-header mb-5 flex flex-row">
                  <div class="flex-auto">
                    <h1 class="ds-heading-display app-h1">
                      Curriculum<em class="ds-grad-text">Finder</em>
                    </h1>
                    <div class="text-sm text-mute mt-2">
                      Filter institutions by state, level and control.
                    </div>
                    <!-- Toggle sits below the subtitle, left-aligned — same spot
                         as the name-search view so it doesn't move when toggling. -->
                    <ViewToggle
                      class="mt-3"
                      :model-value="activeView"
                      @update:model-value="setView"
                    />
                  </div>
                  <div class="flex-none">
                    <AccountPanel />
                  </div>
                </header>

                <FilterBar v-model:filters="filters" class="filter-card__bar" />

                <div class="filter-card__table flex-1 min-h-0 overflow-hidden mt-4">
                  <CollegeTable
                    :filters="filters"
                    :allow-catalogs="true"
                    @select-catalog="onFilterCatalogSelected"
                  />
                </div>
              </div>
            </div>
          </div>

          <template v-else>
          <div
            class="college-search col-span-12 md:col-span-6 lg:col-span-3 flex flex-col"
          >
            <div class="app-card flex flex-col flex-1 overflow-hidden">
              <div class="app-card__body">
                <header class="app-header mb-6 flex flex-row">
                  <div class="flex-auto">
                    <h1 class="ds-heading-display app-h1">
                      Curriculum<em class="ds-grad-text">Finder</em>
                    </h1>
                    <div class="text-sm text-mute mt-2">Search and view college catalogs.</div>
                    <ViewToggle
                      v-if="!isSingleCatalogPage"
                      class="mt-3"
                      :model-value="activeView"
                      @update:model-value="setView"
                    />
                    <button
                      v-if="activeView === 'filter' && selectedCatalog"
                      type="button"
                      class="ds-link back-to-results mt-3"
                      @click="backToResults"
                    >
                      <svg class="back-to-results__arrow" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M19 12H5M11 6l-6 6 6 6" />
                      </svg>
                      Back to results
                    </button>
                  </div>
                  <div class="flex-none">
                    <!-- <UserMenu /> -->
                  </div>
                </header>

                <div class="flex flex-col">
                  <!-- @submit.prevent even though nothing submits this form:
                       it holds a single input, so the browser fires a native
                       submit when the user presses Enter, and with no action
                       that reloads the page and wipes the search. -->
                  <form
                    v-if="compareMode || (activeView === 'name' && !isSingleCatalogPage)"
                    class="ds-stack"
                    @submit.prevent
                  >
                    <!-- AI chat comparison: while it's on, the school search
                         and edition chips add catalogs instead of opening them. -->
                    <div v-if="compareMode" class="compare-banner" role="status">
                      <CatalogRibbon :tone="nextTone" size="lg" :hollow="compareSet.length < 3" />
                      <div class="compare-banner__text">
                        <strong>{{ compareBanner.title }}</strong>
                        <span>{{ compareBanner.text }}</span>
                      </div>
                      <button
                        v-if="addingCompare"
                        type="button"
                        class="ds-link compare-banner__cancel"
                        @click="cancelCompareAdd"
                      >
                        Cancel
                      </button>
                    </div>
                    <div class="field-group">
                      <label for="institution-search" class="sr-only">
                        Find your school
                      </label>
                      <div class="institution-search">
                        <svg
                          class="institution-search__icon"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <circle cx="11" cy="11" r="7" />
                          <path d="M21 21l-4.3-4.3" />
                        </svg>
                        <AutoComplete
                          ref="institutionSearch"
                          input-id="institution-search"
                          v-model="selectedInstitution"
                          :suggestions="institutionSuggestions"
                          :loading="isLoadingInstitutions"
                          @complete="searchInstitutions"
                          @item-select="onInstitutionSelected"
                          @focus="primeInstitutions"
                          option-label="institutionName"
                          placeholder="Search by school name"
                          force-selection
                          :delay="120"
                          :min-length="2"
                          :pt="{
                            root: { class: 'institution-search__root' },
                            container: { class: 'institution-search__container' },
                            input: { class: 'institution-search__input' },
                            panel: { class: 'institution-search__panel' },
                            list: { class: 'institution-search__list' },
                          }"
                        >
                          <template #option="slotProps">
                            <div class="flex items-baseline justify-between gap-3">
                              <span class="truncate">
                                {{ slotProps.option.institutionName }}
                              </span>
                              <span class="text-xs text-mute flex-none">
                                {{ slotProps.option.state }}
                              </span>
                            </div>
                          </template>
                          <template #empty>
                            <span class="text-sm text-mute">No matching schools</span>
                          </template>
                        </AutoComplete>
                      </div>
                    </div>

                    <div class="field-group" v-if="selectedCollege">
                      <span class="ds-field-label">
                        Select catalog edition to start searching
                      </span>
                      <div
                        v-if="isLoadingCatalogs"
                        class="catalog-chips"
                        aria-busy="true"
                      >
                        <span
                          v-for="i in 3"
                          :key="i"
                          class="catalog-chip catalog-chip--skeleton"
                          aria-hidden="true"
                        ></span>
                      </div>
                      <div
                        v-else-if="recentCatalogs.length"
                        class="catalog-chips"
                        role="group"
                        aria-label="Most recent academic years"
                      >
                        <button
                          v-for="catalog in recentCatalogs"
                          :key="catalog.id"
                          type="button"
                          class="catalog-chip"
                          :class="{
                            'catalog-chip--active':
                              !compareMode && selectedCatalog && selectedCatalog.id === catalog.id,
                            'catalog-chip--compared': compareMode && compareItem(catalog.id),
                          }"
                          :aria-pressed="
                            (compareMode ? !!compareItem(catalog.id) : selectedCatalog && selectedCatalog.id === catalog.id)
                              ? 'true'
                              : 'false'
                          "
                          :disabled="compareMode && catalog.format !== 'PDF'"
                          :title="compareMode && catalog.format !== 'PDF' ? 'Web catalogs can\'t be compared' : null"
                          @click="pickCatalog(catalog)"
                        >
                          <CatalogRibbon
                            v-if="compareMode && compareItem(catalog.id)"
                            :tone="compareItem(catalog.id).tone"
                            size="sm"
                          />
                          {{ catalog.edition }}
                          <svg
                            v-if="catalog.format === 'Web'"
                            class="catalog-chip__external"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <path d="M7 17L17 7M9 7h8v8" />
                          </svg>
                        </button>
                      </div>
                      <p v-else class="text-sm text-mute">
                        No catalogs available for this college.
                      </p>

                      <div
                        v-if="archivedCatalogs.length"
                        class="archive-picker"
                      >
                        <label for="archive-catalog" class="ds-field-label">
                          Older years
                        </label>
                        <select
                          id="archive-catalog"
                          :value="archivedSelectionId"
                          @change="onArchivePick"
                          class="ds-select"
                        >
                          <option value="" disabled>
                            {{ archivedCatalogs.length }} more in the archive…
                          </option>
                          <option
                            v-for="catalog in archivedCatalogs"
                            :key="catalog.id"
                            :value="catalog.id"
                          >
                            {{ catalog.edition }}{{ catalog.format === 'Web' ? ' (web)' : '' }}
                          </option>
                        </select>
                      </div>

                      <!-- Re-open link for Web catalogs (chip already opens once) -->
                      <a
                        v-if="selectedCatalogFormat === 'Web'"
                        :href="selectedCatalogUrl"
                        target="_blank"
                        class="ds-button ds-button--primary ds-button--block mt-3"
                      >
                        Reopen this Web Catalog
                        <svg
                          class="ds-button__arrow"
                          aria-hidden="true"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <path d="M5 12h14M13 6l6 6-6 6" />
                        </svg>
                      </a>
                    </div>
                  </form>

                  <!-- Search UI -->
                  <form
                    @submit.prevent="executeSearch"
                    v-if="
                      selectedCatalogFormat === 'PDF' || isSingleCatalogPage
                    "
                    class="search-ui ds-stack mt-6"
                  >
                    <label
                      for="search"
                      class="ds-field"
                    >
                      <span class="ds-field-label">Search this catalog</span>
                      <span class="ds-input-wrap">
                        <input
                          id="search"
                          v-model="searchTerm"
                          type="text"
                          placeholder="e.g. transfer credit"
                        />
                      </span>
                    </label>

                    <div class="flex flex-row flex-wrap gap-2">
                      <!-- No @click here: type="submit" already triggers the
                           form's @submit.prevent="executeSearch". Having both
                           ran the search twice per click, which double-counted
                           every catalog_search event in GA4. -->
                      <button
                        type="submit"
                        class="ds-button ds-button--primary"
                        style="flex:1"
                      >
                        <template v-if="searchStatus === 'IN_PROGRESS'">
                          <svg
                            class="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <circle
                              class="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              stroke-width="4"
                            ></circle>
                            <path
                              class="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        </template>
                        Search
                      </button>

                      <template
                        v-if="
                          selectedCatalogFormat === 'PDF' && searchPerformed
                        "
                      >
                        <button
                          @click="searchPrevious"
                          type="button"
                          class="ds-button ds-button--secondary"
                        >
                          Prev
                        </button>

                        <button
                          @click="searchNext"
                          type="button"
                          class="ds-button ds-button--secondary"
                        >
                          Next
                        </button>

                        <button
                          @click="searchClear"
                          type="button"
                          class="ds-button ds-button--ghost"
                        >
                          Clear
                        </button>
                      </template>
                    </div>
                  </form>

                  <!-- Search Results -->
                  <div
                    class="search-results flex flex-row flex-wrap gap-2 mt-3"
                    v-if="
                      (selectedCatalogFormat === 'PDF' ||
                        isSingleCatalogPage) &&
                      searchPerformed
                    "
                  >
                    <span
                      class="ds-pill"
                      v-if="currentResult"
                    >
                      Page {{ currentResult.pageNumber }}
                    </span>
                    <span class="ds-pill ds-pill--ink">
                      {{ totalResults }} results
                    </span>
                  </div>

                </div>
              </div>

              <div class="sidebar-account">
                <AccountPanel class="account-panel--sidebar" />
              </div>

              <footer class="app-footer app-card__footer flex flex-col">
                <div class="text-xs text-mute mb-4">
                  <p class="mb-2">
                    Search using keywords, course numbers, subjects or titles.
                    Note that course subject code and number punctuation may
                    change from year to year.
                  </p>
                  <p class="mb-2">
                    <em>
                      Can't find what you're looking for? Please let us know!
                      <a
                        href="mailto:contact@degreedata.com"
                        class="ds-link"
                        >contact@degreedata.com</a
                      >
                    </em>
                  </p>
                </div>

                <a href="https://degreedata.com" target="_blank" class="mb-3">
                  <!-- Bundled locally rather than hotlinked from the WordPress
                       uploads folder — that site is being decommissioned. -->
                  <img
                    :src="logoSrc"
                    alt="DegreeData Logo"
                    class="w-32 flex-none"
                  />
                </a>
                <div
                  class="flex justify-between flex-row text-xs text-mute"
                >
                  <span> &copy; {{ currentYear }} DegreeData, LLC </span>
                  <a
                    href="https://degreedata.com/privacy-policy/"
                    class="ds-link"
                  >
                    Privacy Policy
                  </a>
                </div>
              </footer>
            </div>
          </div>
          <div
            class="course-details relative col-span-12 md:col-span-6 lg:col-span-9 flex flex-col gap-3"
          >
            <CompareShelf
              v-if="aiChatEnabled"
              :items="shelfItems"
              :active-id="selectedCatalog.id"
              :adding="addingCompare"
              :next-tone="nextTone"
              @select="openCompareItem(compareItem($event))"
              @remove="removeFromCompare"
              @add="startCompare"
              @cancel="cancelCompareAdd"
            />
            <div class="course-details__row flex flex-1 min-h-0 flex-col lg:flex-row gap-4">
            <div class="app-card flex-1 overflow-hidden">
              <div class="h-full">
                <div class="h-full">
                  <!-- PDF Viewer or iFrame based on the selected catalog -->
                  <div id="adobe-dc-view" class="h-full" :key="pdfViewerKey">
                    <div
                      class="intro-copy hidden md:flex flex-col max-w-3xl mt-6 pl-6 pr-6 h-full"
                      :class="{ 'opacity-0': hasStarted }"
                    >
                      <h2 class="ds-heading mb-2">
                        How to use CurriculumFinder
                      </h2>
                      <IntroContent />
                    </div>
                    <div
                      v-if="hasStarted && selectedCatalogFormat === 'PDF'"
                      class="h-full"
                    >
                      <!-- PDF Viewer will be mounted here -->
                    </div>
                    <div
                      v-if="!selectedCatalog"
                      class="md:hidden flex items-center justify-center h-full text-mute"
                    >
                      Select a catalog to view
                    </div>

                    <Dialog
                      v-model:visible="showMobileIntro"
                      modal
                      class="md:hidden"
                      :style="{ width: '90vw' }"
                    >
                      <template #header>
                        <h2 class="ds-heading">
                          How to use CurriculumFinder
                        </h2>
                      </template>
                      <div class="intro-copy flex flex-col">
                        <IntroContent @close="showMobileIntro = false" />
                      </div>
                    </Dialog>
                  </div>
                </div>
              </div>
            </div>
            <!-- AI chat proof of concept: staging and local dev only (aiChatEnabled). -->
            <AiChatPanel
              v-if="aiChatEnabled"
              :catalogs="aiCatalogs"
              @goto-page="gotoPage"
            />
            </div>
          </div>
          </template>
        </template>
      </template>
    </div>

    <div class="max-w-2xl mx-auto"></div>
  </div>
</template>

<script>
import IntroContent from "./components/IntroContent.vue";
import LoaderComponent from "./components/Loader.vue";
import ViewToggle from "./components/ViewToggle.vue";
import FilterBar from "./components/FilterBar.vue";
import CollegeTable from "./components/CollegeTable.vue";
import PricingPlans from "./components/PricingPlans.vue";
import AccountPanel from "./components/AccountPanel.vue";
import AiChatPanel from "./components/AiChatPanel.vue";
import CompareShelf from "./components/ai/CompareShelf.vue";
import CatalogRibbon from "./components/ai/CatalogRibbon.vue";
import LandingIntro from "./components/LandingIntro.vue";
import { trackTrialSignup } from "./analytics/xad";

const AI_CHAT_HOSTS = ["staging-curriculumfinder.degreedata.com", "localhost", "127.0.0.1"];
import {
  AuthPanel,
  getAccountSummary,
  getMemberDivision,
  hasActiveAccess,
  isPlanTier,
  joinTeamWithToken,
  startTierCheckout,
} from "./auth";
import { getAllInstitutions } from "./api/institutions";
import logoSrc from "@/assets/degreedata-logo.png";

export default {
  components: {
    IntroContent,
    LoaderComponent,
    ViewToggle,
    FilterBar,
    CollegeTable,
    PricingPlans,
    AccountPanel,
    AiChatPanel,
    CompareShelf,
    CatalogRibbon,
    AuthPanel,
    LandingIntro,
  },
  /* global AdobeDC */

  data() {
    return {
      logoSrc,
      // Which of the two toggleable views is active: 'name' (search by school
      // name) or 'filter' (filter/sort by state, level, control).
      activeView: "name",
      // Filter model shared by FilterBar (controls) and CollegeTable (results).
      // Selecting a state drives the API fetch; the rest filter rows client-side.
      filters: {
        state: "",
        level: "",
        control: "",
      },
      selectedCollegeName: "",
      selectedCatalogYear: "",
      selectedPdf: null,
      adobeViewer: null,
      searchTerm: "",
      searchStatus: null,
      isSingleCatalogPage: false,
      searchResults: [],
      searchPerformed: false,
      currentResult: null,
      totalResults: 0,
      selectedState: "",
      selectedCollege: "",
      colleges: [],
      catalogs: [],
      selectedCatalog: "",
      selectedCatalogFormat: "",
      selectedCatalogUrl: "",
      pdfViewerKey: 0,
      isLoadingCatalogs: false,
      showMobileIntro: false,
      hasStarted: false,
      isAuthenticated: false,
      canViewCatalogs: false,
      initialLoading: true,
      // Set from a ?invite=<token> link; drives AuthPanel "join a team" mode and
      // (for already-logged-in visitors) an auto-join on load.
      inviteToken: "",
      selectedInstitution: null,
      institutionSuggestions: [],
      allInstitutions: [],
      isLoadingInstitutions: false,
      institutionsPrimed: false,
      // Catalog id from a ?catalogId= deep link, kept until the access check
      // passes. Survives a signup/login so the visitor lands on the catalog
      // they clicked rather than the bare app.
      pendingCatalogId: "",
      // Tier slug from a ?plan= "Buy now" link on the marketing pricing page.
      // Cleared once checkout has been handed off (or ruled out).
      pendingPlanTier: "",
      // True from the moment checkout is requested until Memberstack navigates
      // away, so the gate renders a hand-off message instead of app chrome.
      startingCheckout: false,
      authInitialTab: "signup",
      // AI chat comparison (staging): up to 3 catalogs, each
      // { id, catalog, college, edition, tone }. Empty when viewing just one.
      compareSet: [],
      // The user asked to add a catalog: the sidebar chips add, not open.
      addingCompare: false,
      // Page to jump to once the viewer finishes opening another compared catalog.
      pendingGotoPage: null,
    };
  },
  async beforeMount() {
    const currentHost = window.location.hostname;

    // Redirect logic
    if (currentHost === "curriculumsearch.degreedata.com") {
      window.location.href =
        "https://quicksearch.degreedata.com" +
        window.location.pathname +
        window.location.search;
      return; // Stop further execution
    }

    const searchParams = new URLSearchParams(window.location.search);
    // Deep link from the public CoverageReport app, which links every catalog —
    // PDF *and* Web — by id and never by URL. Held here until refreshAccess()
    // resolves, then opened by openDeepLinkedCatalog().
    this.pendingCatalogId = searchParams.get("catalogId") || "";
    this.inviteToken = searchParams.get("invite") || "";

    // The pricing page has two CTAs per tier: the trial link carries ?signup=1
    // and "Buy now" carries ?plan=<tier>. An unknown tier is ignored so a
    // mistyped link just lands on the normal sign-up screen.
    const tier = (searchParams.get("plan") || "").toLowerCase();
    this.pendingPlanTier = isPlanTier(tier) ? tier : "";
    this.authInitialTab =
      searchParams.get("login") && !searchParams.get("signup")
        ? "login"
        : "signup";

    await this.refreshAccess();

    // If a logged-in visitor opened a team invite link, attach them to the team.
    if (this.inviteToken && this.isAuthenticated) {
      await this.acceptInvite();
    }

    // Someone who clicked "Buy now" while already signed in should go to
    // checkout, not into the app.
    if (this.pendingPlanTier && this.isAuthenticated) {
      await this.startPendingCheckout();
      if (this.startingCheckout) return;
    }

    await this.openDeepLinkedCatalog();
  },
  methods: {
    async primeInstitutions() {
      if (this.institutionsPrimed || this.isLoadingInstitutions) return;
      this.isLoadingInstitutions = true;
      try {
        this.allInstitutions = await getAllInstitutions();
        this.institutionsPrimed = true;
      } catch (error) {
        console.error("Error loading institutions list:", error);
      } finally {
        this.isLoadingInstitutions = false;
      }
    },

    async searchInstitutions({ query }) {
      if (!this.institutionsPrimed) await this.primeInstitutions();
      const q = (query ?? "").trim().toLowerCase();
      if (!q) {
        this.institutionSuggestions = this.allInstitutions.slice(0, 25);
        return;
      }
      const matches = [];
      for (const inst of this.allInstitutions) {
        if (inst.institutionName.toLowerCase().includes(q)) {
          matches.push(inst);
          if (matches.length >= 50) break;
        }
      }
      this.institutionSuggestions = matches;
    },

    async onInstitutionSelected({ value }) {
      const institution = value;
      if (!institution || !institution.id) return;

      this.hasStarted = true;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "institution_search_select",
        institution: institution.institutionName,
        state: institution.state,
      });

      this.selectedState = institution.state;
      // Seed `colleges` with just the picked school so onCollegeChange can
      // resolve its name, then trigger the catalog fetch immediately. We no
      // longer fetch the whole state's college list (the dropdown is gone) —
      // that extra round-trip was the multi-second gap where nothing appeared
      // before the catalog-edition skeleton showed.
      this.colleges = [
        { id: institution.id, institutionName: institution.institutionName },
      ];
      this.selectedCollege = institution.id;
    },

    // Switch between the 'name' and 'filter' views. Each view starts clean, so
    // we tear down any open catalog/viewer when toggling.
    setView(view) {
      if (view === this.activeView) return;
      this.activeView = view;
      this.resetSelection();

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "view_toggle", view });

      if (
        view === "name" &&
        this.canViewCatalogs &&
        !this.isSingleCatalogPage &&
        this.isDesktop
      ) {
        this.$nextTick(() => this.focusInstitutionSearch());
      }
    },

    // Return from an open catalog back to the filter results table. Keeps the
    // active view ('filter') and the filter selections so the table re-renders.
    backToResults() {
      this.resetSelection();
    },

    // Clear everything tied to a selected catalog / open viewer. Does NOT touch
    // `filters`, so the filter/sort results survive a "back to results".
    resetSelection() {
      this.compareSet = [];
      this.addingCompare = false;
      this.pendingGotoPage = null;
      this.adobeViewer = null;
      this.selectedPdf = null;
      this.selectedCatalog = "";
      this.selectedCatalogFormat = "";
      this.selectedCatalogUrl = "";
      this.selectedCatalogYear = "";
      this.searchObject = null;
      this.searchPerformed = false;
      this.searchTerm = "";
      this.searchStatus = null;
      this.currentResult = null;
      this.totalResults = 0;
      this.hasStarted = false;
      this.pdfViewerKey++;
      // Name-view specifics
      this.selectedInstitution = null;
      this.selectedCollege = "";
      this.selectedCollegeName = "";
      this.catalogs = [];
    },

    // A catalog was chosen from the filter/sort results table. Web catalogs
    // open in a new tab and we stay on the table; PDF catalogs open in the
    // shared in-app viewer, just like the name-search flow.
    onFilterCatalogSelected({ catalog, institution }) {
      if (!catalog) return;

      if (catalog.format === "Web" && catalog.catalogUrl) {
        window.open(catalog.catalogUrl, "_blank", "noopener,noreferrer");
        return;
      }

      this.selectedState = institution.state;
      this.selectedCollegeName = institution.institutionName;
      this.selectedCatalogYear = catalog.edition;
      this.hasStarted = true;
      this.selectCatalog(catalog);
    },

    // Open a ?catalogId= deep link — but only for a visitor who has passed the
    // access check. CoverageReport is public and links catalogs by id only, so
    // this is the single point where a paid catalog is resolved and revealed:
    // a PDF loads in the viewer, a Web catalog's URL is handed over. Bail while
    // unauthorized and keep `pendingCatalogId`, so onAuthorized() can retry
    // after a successful signup/login.
    async openDeepLinkedCatalog() {
      if (!this.pendingCatalogId || !this.canViewCatalogs) return;

      const catalogId = this.pendingCatalogId;
      const response = await this.fetchCatalog(catalogId);
      // The endpoint wraps the record in `message` — unwrap it, or every
      // downstream read of .format / .edition / .s3Id is undefined.
      const catalog = response && response.message;
      if (!catalog) return;

      this.pendingCatalogId = "";
      this.isSingleCatalogPage = true;
      // Assigning selectedCatalog fires the watcher -> onCatalogChange(), which
      // is what actually loads a PDF into the viewer. Don't call loadPdf() here
      // as well, or Adobe mounts twice into #adobe-dc-view.
      this.selectedCatalog = catalog;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "catalog_deep_link_opened",
        catalog: {
          catalogId,
          edition: catalog.edition,
          format: catalog.format,
        },
      });

      // Web catalogs live on the institution's own site — send the visitor
      // straight there now that they're through the gate.
      if (catalog.format === "Web") {
        if (catalog.catalogUrl) {
          window.location.replace(catalog.catalogUrl);
        }
        return;
      }

      // PDF: onCatalogChange() (via the watcher above) does the loading.
      this.hasStarted = true;
    },

    async fetchCatalog(catalogId) {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/catalogs?id=${catalogId}`,
          {
            method: "GET",
            headers: {
              "x-api-key": process.env.VUE_APP_API_KEY,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        // console.log('Catalog Response:', response);
        const data = await response.json();
        return data;
      } catch (e) {
        console.error("Catalog Error:", e);
        return null;
      }
    },

    async onCollegeChange() {
      // While comparing, picking another school only lists its catalogs: the
      // open catalog stays put until one is added.
      if (this.compareMode) {
        this.catalogs = [];
        if (this.selectedCollege) await this.loadCollegeCatalogs();
        return;
      }
      // Clean up existing state first
      if (this.adobeViewer) {
        this.adobeViewer = null;
      }
      this.selectedPdf = null;
      this.selectedCatalogFormat = "";
      this.selectedCatalogUrl = "";
      this.selectedCatalog = "";
      this.catalogs = [];
      this.pdfViewerKey++; // Force PDF viewer component recreation

      if (!this.selectedCollege) return;

      const selectedCollegeObj = this.colleges.find(
        (college) => college.id === this.selectedCollege
      );
      this.selectedCollegeName = selectedCollegeObj
        ? selectedCollegeObj.institutionName
        : "";

      await this.loadCollegeCatalogs();
    },

    // Fetch the selected college's catalogs, most recent edition first.
    async loadCollegeCatalogs() {
      this.isLoadingCatalogs = true;
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE_URL}/catalogs/institution?id=${this.selectedCollege}`,
          {
            method: "GET",
            headers: {
              "x-api-key": process.env.VUE_APP_API_KEY,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        );
        let data = await response.json();

        if (data.message && Array.isArray(data.message)) {
          // Most recent edition first (mirrors CoverageReport sibling)
          data.message.sort(
            (a, b) => parseInt(b.edition, 10) - parseInt(a.edition, 10)
          );
          this.catalogs = data.message;
        } else {
          console.error(
            "Expected array in message property but got:",
            typeof data.message
          );
          this.catalogs = [];
        }
      } catch (error) {
        console.error("Error fetching catalogs:", error);
        this.catalogs = [];
      } finally {
        this.isLoadingCatalogs = false;
      }
    },

    selectCatalog(catalog) {
      if (!catalog) return;
      this.hasStarted = true;
      this.selectedCatalog = catalog;
      // selectedCatalog watcher fires onCatalogChange, which handles PDF load
      // and Web URL bookkeeping. For Web catalogs we also open the tab now
      // since the click is a direct user gesture.
      if (catalog.format === "Web" && catalog.catalogUrl) {
        window.open(catalog.catalogUrl, "_blank", "noopener,noreferrer");
      }
    },

    onArchivePick(event) {
      const id = event.target.value;
      if (!id) return;
      const catalog = this.catalogs.find((c) => String(c.id) === String(id));
      if (catalog) this.pickCatalog(catalog);
    },

    async onCatalogChange() {
      if (!this.selectedCatalog) return;

      const selectedCatalogObj = this.catalogs.find(
        (catalog) => catalog === this.selectedCatalog
      );
      // Fall back to the catalog's own edition for catalogs that didn't come
      // from this.catalogs (e.g. picked from the filter/sort results table).
      this.selectedCatalogYear = selectedCatalogObj
        ? selectedCatalogObj.edition
        : this.selectedCatalog.edition || "";

      this.$nextTick(async () => {
        try {
          if (this.selectedCatalog.format === "PDF") {
            this.selectedCatalogFormat = "PDF";
            this.loadPdf(
              `${process.env.VUE_APP_S3_BASE_URL}/${this.selectedCatalog.s3Id}`
            );
          } else if (this.selectedCatalog.format === "Web") {
            this.selectedCatalogFormat = "Web";
            this.selectedCatalogUrl = this.selectedCatalog.catalogUrl;
          }

          // Push event to dataLayer
          window.dataLayer.push({
            event: "catalog_selected",
            state: this.selectedState,
            college: this.selectedCollegeName,
            catalogYear: this.selectedCatalogYear,
          });
        } catch (error) {
          console.error("Error loading PDF:", error);
        }
      });
    },

    loadPdf(url) {
      this.$nextTick(() => {
        this.selectedPdf = url;

        const viewerConfig = {
          defaultViewMode: "FIT_WIDTH",
          showAnnotationTools: false,
          showDownloadPDF: false,
          showPrintPDF: false,
          enableSearchAPIs: true,
        };

        const adobeDCView = new AdobeDC.View({
          clientId: process.env.VUE_APP_ADOBE_CLIENT_ID,
          divId: "adobe-dc-view",
        });

        adobeDCView
          .previewFile(
            {
              content: {
                location: { url: this.selectedPdf },
              },
              metaData: {
                fileName: `${this.selectedCollegeName} - ${this.selectedCatalogYear}.pdf`,
              },
            },
            viewerConfig
          )
          .then((adobeViewer) => {
            this.adobeViewer = adobeViewer;
            // A citation from another compared catalog asked for this page.
            if (this.pendingGotoPage) {
              const page = this.pendingGotoPage;
              this.pendingGotoPage = null;
              adobeViewer.getAPIs().then((apis) => apis.gotoLocation(page));
            }
          });

        // Add focus tracking for the PDF viewer
        const pdfViewElement = document.getElementById("adobe-dc-view");
        pdfViewElement.addEventListener("focus", () => {
          window.dataLayer.push({
            event: "pdf_view_focus",
            catalogYear: this.selectedCatalogYear,
            college: this.selectedCollegeName,
            state: this.selectedState,
          });
        });
      });
    },

    // Page chips in AiChatPanel. Page numbers are 1-based PDF page indexes,
    // which is what gotoLocation() takes.
    // Page chips in AiChatPanel: { catalogId, page }. Page numbers are 1-based
    // PDF page indexes, which is what gotoLocation() takes. A chip from another
    // compared catalog opens that catalog first, then jumps once it's loaded.
    gotoPage({ catalogId, page }) {
      if (this.selectedCatalog && String(this.selectedCatalog.id) === String(catalogId)) {
        if (this.adobeViewer) this.adobeViewer.getAPIs().then((apis) => apis.gotoLocation(page));
        return;
      }
      const item = this.compareItem(catalogId);
      if (!item) return;
      this.pendingGotoPage = page;
      this.openCompareItem(item);
    },

    // ---- AI chat comparison (staging) ----

    compareItem(id) {
      return this.compareSet.find((it) => String(it.id) === String(id)) || null;
    },

    // The open catalog as a shelf item. Deep links don't carry the college
    // name, so fall back to the PDF's file name.
    currentCompareItem() {
      const c = this.selectedCatalog;
      const fromFile = (c.s3Id || "").replace(/\.pdf$/i, "").replace(/_/g, " ").replace(/\s*\d{4}(-\d{2,4})?$/, "");
      return {
        id: c.id,
        catalog: c,
        college: this.selectedCollegeName || fromFile || "This catalog",
        edition: this.selectedCatalogYear || c.edition || "",
        tone: 0,
      };
    },

    startCompare() {
      if (!this.compareSet.length) this.compareSet = [this.currentCompareItem()];
      this.addingCompare = true;
    },

    cancelCompareAdd() {
      this.addingCompare = false;
      // Never got past one catalog: back to the ordinary single view.
      if (this.compareSet.length < 2) this.compareSet = [];
    },

    // A sidebar chip: opens the catalog normally, or, while comparing, adds it
    // (or switches to it if it's already in the comparison).
    pickCatalog(catalog) {
      if (!this.compareMode) {
        this.selectedCollegeName = this.browsingCollegeName;
        this.selectCatalog(catalog);
        return;
      }
      const existing = this.compareItem(catalog.id);
      if (existing) {
        this.openCompareItem(existing);
        return;
      }
      if (catalog.format !== "PDF" || this.compareSet.length >= 3) return;
      const item = {
        id: catalog.id,
        catalog,
        college: this.browsingCollegeName,
        edition: catalog.edition,
        tone: this.nextTone,
      };
      this.compareSet = [...this.compareSet, item];
      this.addingCompare = false;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "ai_compare_add", catalogs: this.compareSet.length });
      this.openCompareItem(item);
    },

    openCompareItem(item) {
      if (!item) return;
      this.selectedCollegeName = item.college;
      this.selectedCatalogYear = item.edition;
      this.hasStarted = true;
      if (!this.selectedCatalog || this.selectedCatalog.id !== item.id) this.selectCatalog(item.catalog);
    },

    removeFromCompare(id) {
      const wasActive = this.selectedCatalog && String(this.selectedCatalog.id) === String(id);
      const rest = this.compareSet.filter((it) => String(it.id) !== String(id));
      // One left is just a catalog, not a comparison.
      this.compareSet = rest.length > 1 ? rest : [];
      if (wasActive && rest.length) this.openCompareItem(rest[0]);
      else if (rest.length === 1) {
        this.selectedCollegeName = rest[0].college;
        this.selectedCatalogYear = rest[0].edition;
      }
    },

    executeSearch() {
      if (!this.adobeViewer || this.searchTerm.length === 0) {
        return;
      }

      window.dataLayer.push({
        event: "catalog_search",
        searchTerm: this.searchTerm,
        catalogYear: this.selectedCatalogYear,
        college: this.selectedCollegeName,
        state: this.selectedState,
      });

      this.adobeViewer.getAPIs().then((apis) => {
        apis
          .search(this.searchTerm)
          .then((searchObject) => {
            this.searchObject = searchObject;
            searchObject
              .onResultsUpdate(this.onResultsUpdate)
              .catch(this.onSearchFail);
            this.searchPerformed = true;
          })
          .catch(this.onSearchFail);
      });
    },

    // Method to handle search results update
    onResultsUpdate(result) {
      // console.log(result);

      this.searchStatus = result.status;
      this.currentResult = result.currentResult;
      this.totalResults = result.totalResults;
    },

    // Method to handle search failures
    onSearchFail(error) {
      console.error("Search failed:", error);
    },

    // Additional methods for navigating search results
    searchNext() {
      if (this.searchObject) {
        this.searchObject.next();
      }
    },

    searchPrevious() {
      if (this.searchObject) {
        this.searchObject.previous();
      }
    },

    searchClear() {
      if (this.searchObject) {
        this.searchObject.clear();
      }
      this.searchTerm = "";
      this.searchPerformed = false;
      this.searchStatus = null;
    },
    async refreshAccess() {
      this.initialLoading = true;
      // Local testing only: VUE_APP_DEV_BYPASS_AUTH=1 in .env.local (gitignored,
      // never set on Cloudflare) skips the Memberstack gate, and only on a
      // localhost hostname, so a stray build can't open the gate anywhere else.
      if (
        process.env.VUE_APP_DEV_BYPASS_AUTH === "1" &&
        ["localhost", "127.0.0.1"].includes(window.location.hostname)
      ) {
        this.isAuthenticated = true;
        this.canViewCatalogs = true;
        this.initialLoading = false;
        return;
      }
      try {
        const { authenticated, authorized } = await hasActiveAccess();
        this.isAuthenticated = authenticated;
        this.canViewCatalogs = authorized;
      } finally {
        this.initialLoading = false;
      }
      if (this.isAuthenticated) {
        this.reportDivision();
      }
      if (
        this.canViewCatalogs &&
        !this.isSingleCatalogPage &&
        this.isDesktop
      ) {
        this.$nextTick(() => this.focusInstitutionSearch());
      }
    },

    // Send the member's division to GA4 as a user-scoped property, so every
    // report can be segmented by it. This runs on each authenticated load, not
    // just at signup: a user property has to be set per session, so setting it
    // once at signup would tag exactly one session per member.
    //
    // Requires a matching User-scoped custom dimension registered in GA4
    // (user property `division`) — without it the value is collected but not
    // reportable. It is also not retroactive: sessions before it existed read
    // "(not set)" permanently.
    //
    // "(not set)" is sent explicitly rather than omitted, because gtag retains
    // previously set user properties for the session — a member with no
    // division could otherwise inherit the last value seen on that page.
    //
    // Use the OBJECT form, gtag("set", { user_properties: ... }). The
    // three-argument gtag("set", "user_properties", ...) form is what Google
    // documents, but on this page it never reaches the wire: verified against
    // production by capturing every /g/collect payload, where events sent after
    // that call carried no `up.division` at all, while the object form below
    // attached it on the very next event. Since the value only has to land
    // once per session for GA4 to hold it against the user, one event carrying
    // it is enough. If you change this line, re-check a real collect payload —
    // a silent failure here reads as "(not set)" forever in reporting.
    async reportDivision() {
      try {
        const division = await getMemberDivision();
        if (typeof window.gtag === "function") {
          window.gtag("set", {
            user_properties: { division: division || "(not set)" },
          });
        }
      } catch (err) {
        console.error("[analytics] reporting division failed:", err);
      }
    },

    focusInstitutionSearch() {
      const ref = this.$refs.institutionSearch;
      if (!ref) return;
      const root = ref.$el || ref;
      const input = root.querySelector
        ? root.querySelector("input")
        : null;
      if (input) input.focus();
    },

    // Ad conversion for Angela's campaigns: a new free trial is the signup the
    // CurriculumFinder ads are paying for. (Purchases and team invites don't
    // count here.)
    onTrialStarted() {
      trackTrialSignup();
    },

    async onAuthorized() {
      await this.refreshAccess();
      if (this.inviteToken) this.clearInviteParam();
      // Arrived on a "Buy now" link: finish the purchase they came for instead
      // of dropping them into the app on their new trial.
      if (this.pendingPlanTier) {
        await this.startPendingCheckout();
        if (this.startingCheckout) return;
      }
      // A visitor who arrived on a ?catalogId= deep link and only just signed
      // up/logged in still expects to land on that catalog.
      await this.openDeepLinkedCatalog();
    },

    // Runs the ?plan= purchase deep link: resolve the tier to a live price and
    // hand off to Stripe. Anything unexpected falls through into the app (or
    // the trial-ended screen, which offers the same plans) rather than
    // dead-ending the buyer.
    async startPendingCheckout() {
      const tier = this.pendingPlanTier;
      this.pendingPlanTier = "";
      try {
        // A subscriber following a stale "Buy now" link must not be sold a
        // second subscription.
        const summary = await getAccountSummary();
        if (summary?.status === "paid") {
          this.clearPlanParam();
          return;
        }
        this.startingCheckout = true;
        // Stripe returns to the current URL when checkout is abandoned, so
        // ?plan= has to go before the redirect — otherwise cancelling bounces
        // straight back into checkout.
        this.clearPlanParam();
        const started = await startTierCheckout(tier);
        if (!started) {
          console.warn(`[pricing] no active price matched plan "${tier}".`);
          this.startingCheckout = false;
        }
      } catch (error) {
        console.error("[pricing] deep-link checkout failed:", error);
        this.startingCheckout = false;
        this.clearPlanParam();
      }
    },

    clearPlanParam() {
      const url = new URL(window.location.href);
      if (url.searchParams.has("plan")) {
        url.searchParams.delete("plan");
        window.history.replaceState({}, "", url);
      }
    },

    // Attach an already-authenticated visitor to a team from an invite link.
    async acceptInvite() {
      try {
        await joinTeamWithToken(this.inviteToken);
      } catch (error) {
        // Already a member, invalid/expired token, or seats full — non-fatal.
        console.warn("[invite] joinTeam failed:", error);
      }
      this.clearInviteParam();
      await this.refreshAccess();
    },

    clearInviteParam() {
      this.inviteToken = "";
      const url = new URL(window.location.href);
      if (url.searchParams.has("invite")) {
        url.searchParams.delete("invite");
        window.history.replaceState({}, "", url);
      }
    },
  },

  watch: {
    selectedCollege(newVal) {
      if (newVal) {
        this.onCollegeChange();
      }
    },
    selectedCatalog(newVal) {
      if (newVal) {
        this.onCatalogChange();
      }
    },
  },

  computed: {
    // Catalog ids must match POC_CATALOG_IDS in functions/api/ask.js.
    // AI chat proof of concept: every PDF catalog, but only on staging and
    // local dev — Anne demos it from staging; production never shows it.
    aiChatEnabled() {
      return (
        AI_CHAT_HOSTS.includes(window.location.hostname) &&
        this.selectedCatalogFormat === "PDF" &&
        !!this.selectedCatalog &&
        !!this.selectedCatalog.s3Id
      );
    },
    // What the AI panel reads: the comparison, or just the open catalog.
    aiCatalogs() {
      const items = this.compareSet.length ? this.compareSet : [this.currentCompareItem()];
      return items.map((it) => ({
        id: it.id,
        pdfUrl: `${process.env.VUE_APP_S3_BASE_URL}/${it.catalog.s3Id}`,
        college: it.college,
        edition: it.edition,
        tone: it.tone,
      }));
    },
    shelfItems() {
      return this.compareSet.length ? this.compareSet : [this.currentCompareItem()];
    },
    // Sidebar chips add to the comparison rather than open a catalog.
    compareMode() {
      return this.addingCompare || this.compareSet.length > 1;
    },
    compareBanner() {
      const n = this.compareSet.length;
      if (this.addingCompare) {
        return {
          title: "Add a catalog to compare",
          text: `Pick another year below, or search for another school. ${n} of 3 chosen.`,
        };
      }
      if (n < 3) {
        return {
          title: `Comparing ${n} catalogs`,
          text: "Pick another year or school to add a third. Remove a catalog above to stop comparing it.",
        };
      }
      return {
        title: "Comparing 3 catalogs",
        text: "That's the most you can compare. Remove one above to add another.",
      };
    },
    // The first ribbon colour not already taken, so colours never shuffle.
    nextTone() {
      const used = this.compareSet.map((it) => it.tone);
      return [0, 1, 2].find((t) => !used.includes(t)) ?? 0;
    },
    // The school whose edition chips the sidebar is showing.
    browsingCollegeName() {
      const c = this.colleges.find((college) => college.id === this.selectedCollege);
      return (c && c.institutionName) || this.selectedCollegeName;
    },
    // True when the filter/sort view should show its full-width browser
    // (horizontal filters + results table). Once a PDF catalog is opened the
    // layout switches to the shared two-pane viewer, so this turns false.
    filterBrowserMode() {
      return (
        this.activeView === "filter" &&
        !this.selectedCatalog &&
        !this.isSingleCatalogPage
      );
    },
    isDesktop() {
      return window.innerWidth >= 768;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    recentCatalogs() {
      return this.catalogs.slice(0, 5);
    },
    archivedCatalogs() {
      return this.catalogs.slice(5);
    },
    archivedSelectionId() {
      if (!this.selectedCatalog) return "";
      return this.archivedCatalogs.some(
        (c) => c.id === this.selectedCatalog.id
      )
        ? this.selectedCatalog.id
        : "";
    },
  },

  mounted() {
    this.showMobileIntro = this.isDesktop === false;
  },
};
</script>

<style>
#app {
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: var(--ink);
  background: var(--wash);
}

.catalog-search-wrapper {
  min-height: 100vh;
  padding: 24px;
}

.catalog-search-wrapper > .grid {
  min-height: calc(100vh - 48px);
}

/* Filter/sort browser: from md up, pin the grid to a definite viewport height
 * so the results table scrolls inside its card and keeps its paginator fixed.
 * minmax(0, 1fr) stops a tall table from inflating the row past the viewport.
 * Below md the layout stacks and the page scrolls normally. */
@media (min-width: 768px) {
  .catalog-search-wrapper > .grid--filter {
    height: calc(100vh - 48px);
    grid-template-rows: minmax(0, 1fr);
  }
}

.app-card {
  background: var(--paper);
  border: 1px solid var(--line);
  border-radius: var(--r-card);
  box-shadow: var(--shadow-card);
}

.app-card__body {
  padding: 28px 28px 20px;
}

/* Pushes the account button + footer to the bottom of the sidebar column
   (moved off .app-card__footer so the button sits just above the footer's
   top border). */
.sidebar-account {
  margin-top: auto;
}

/* Sidebar instance only: align the button with the card content and leave
   room above the footer border. The header instance (Filter & sort view)
   stays inline with no padding. */
.account-panel--sidebar {
  padding: 0 28px 20px;
}

@media (max-width: 767px) {
  .account-panel--sidebar {
    padding: 0 20px 16px;
  }
}

.app-card__footer {
  border-top: 1px solid var(--line);
  padding: 20px 28px 24px;
}

.app-h1 {
  font-size: 30px;
}

.app-h1 em {
  font-style: normal;
}

@media (max-width: 767px) {
  .catalog-search-wrapper {
    padding: 16px;
  }
  .app-card__body {
    padding: 20px 20px 16px;
  }
  .app-card__footer {
    padding: 16px 20px 20px;
  }
  .app-h1 {
    font-size: 26px;
  }
}

.trial-ended {
  width: 100%;
  max-width: 880px;
  text-align: center;
}

.trial-ended__head {
  margin-bottom: 24px;
}

.checkout-handoff {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.intro-copy {
  transition: opacity 0.5s ease;
}

.auth-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  width: 100%;
  max-width: 1080px;
  align-items: stretch;
}

@media (min-width: 1024px) {
  .auth-layout {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 32px;
  }
}


.form-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--mute);
  margin: 2px 0;
}

.form-divider::before,
.form-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: var(--line);
}

.institution-search {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  background: var(--paper);
  border: 1px solid var(--hair);
  border-radius: var(--r-input);
  min-height: 48px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  overflow: hidden;
}

.institution-search:hover {
  border-color: var(--ink-soft);
}

.institution-search:focus-within {
  border-color: var(--ink);
  box-shadow: var(--shadow-focus);
}

.institution-search__icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: var(--mute);
  pointer-events: none;
  z-index: 2;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.8;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.institution-search:focus-within .institution-search__icon {
  color: var(--ink);
}

.institution-search__root {
  display: block;
  width: 100%;
}

.institution-search__input.institution-search__input {
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  padding: 13px 14px 13px 46px;
  font-size: 14.5px;
  font-family: inherit;
  color: var(--ink);
  border-radius: 0;
  box-shadow: none;
}

.institution-search__input.institution-search__input::placeholder {
  color: var(--faint);
}

.institution-search__input.institution-search__input:focus,
.institution-search__input.institution-search__input:hover {
  outline: none;
  box-shadow: none;
  border: none;
}

.institution-search__panel.institution-search__panel {
  background: var(--paper);
  border: 1px solid var(--hair);
  border-radius: var(--r-input);
  margin-top: 4px;
  overflow: hidden;
  box-shadow: 0 12px 28px rgba(20, 14, 40, 0.12),
    0 4px 8px rgba(20, 14, 40, 0.06);
  z-index: 1000;
}

.institution-search__list.institution-search__list {
  max-height: 280px;
  overflow-y: auto;
  padding: 6px 0;
  margin: 0;
  list-style: none;
}

.back-to-results {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  cursor: pointer;
}

.back-to-results__arrow {
  width: 14px;
  height: 14px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  flex: none;
}

/* Filter/sort browser card: header + horizontal filters + scrollable table. */
.filter-card__bar {
  padding-bottom: 4px;
  border-bottom: 1px solid var(--line);
}

.filter-card__table {
  display: flex;
  flex-direction: column;
}

.catalog-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 6px;
}

.catalog-chip {
  appearance: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-size: 14px;
  line-height: 1.2;
  cursor: pointer;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--paper);
  color: var(--ink);
  transition: background 150ms ease, border-color 150ms ease, color 150ms ease;
}

.catalog-chip:hover {
  border-color: var(--ink);
  background: var(--wash);
}

.catalog-chip:focus-visible {
  outline: 2px solid var(--ink);
  outline-offset: 2px;
}

/* In a comparison: this edition is one of the compared catalogs. */
.catalog-chip--compared,
.catalog-chip--compared:hover {
  border-color: var(--ink);
  background: var(--wash);
  font-weight: 600;
}

.catalog-chip:disabled {
  cursor: not-allowed;
  color: var(--faint);
  background: var(--paper);
  border-color: var(--line);
}

/* Sidebar note while the edition chips add to an AI chat comparison. */
.compare-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 0 12px 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--line);
  background: var(--wash);
  font-size: 13px;
  line-height: 1.45;
}
.compare-banner__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 10px;
  color: var(--ink-soft);
}
.compare-banner__text strong {
  color: var(--ink);
  font-weight: 600;
}
.compare-banner__cancel {
  margin-left: auto;
  padding-top: 10px;
  font-size: 13px;
  white-space: nowrap;
}

.catalog-chip--active,
.catalog-chip--active:hover {
  background: var(--ink);
  color: var(--paper);
  border-color: var(--ink);
}

.catalog-chip__external {
  width: 12px;
  height: 12px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  opacity: 0.7;
}

.catalog-chip--skeleton {
  pointer-events: none;
  min-width: 84px;
  height: 34px;
  background: var(--wash);
  border-color: var(--line);
  color: transparent;
  animation: catalog-chip-pulse 1.2s ease-in-out infinite;
}

@keyframes catalog-chip-pulse {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

.archive-picker {
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
